import React, { useState, useEffect } from 'react';
import { db } from '../../firebase';
import { doc, setDoc, deleteField, onSnapshot, updateDoc } from 'firebase/firestore';
import './ManageTables.css';
import { 
  FaTable,
  FaTrash, 
  FaSave, 
  FaSpinner,
  FaExclamationTriangle,
  FaCheckCircle,
  FaInfoCircle,
  FaHistory,
  FaEdit,
  FaUndo,
  FaCopy,
  FaDownload,
} from 'react-icons/fa';

const ManageTables = () => {
  const [nbTables, setNbTables] = useState(null);
  const [loading, setLoading] = useState(true);
  const [inputValue, setInputValue] = useState('');
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState(''); // 'success', 'error', 'info'
  const [showHistory, setShowHistory] = useState(false);
  const [history, setHistory] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [setPreviousValue] = useState(null);

  // Écoute des modifications en temps réel sur "nb_table" dans Firestore
  useEffect(() => {
    const docRef = doc(db, 'settings', 'nb_table');
    const unsubscribe = onSnapshot(
      docRef,
      (docSnap) => {
        if (docSnap.exists()) {
          setNbTables(docSnap.data().value || null);
        } else {
          setNbTables(null);
        }
        setLoading(false);
      },
      (error) => {
        console.error('Erreur lors de l\'écoute des modifications :', error);
        setLoading(false);
      }
    );

    return () => unsubscribe(); // Nettoyage de l'écouteur lors du démontage
  }, []);

  // Fonction pour afficher un message
  const showMessage = (text, type) => {
    setMessage(text);
    setMessageType(type);
    setTimeout(() => {
      setMessage('');
      setMessageType('');
    }, 5000);
  };

  // Fonction pour valider l'input
  const validateInput = (value) => {
    const num = parseInt(value, 10);
    if (isNaN(num) || num < 1 || num > 100) {
      return 'Veuillez entrer un nombre entre 1 et 100.';
    }
    return null;
  };

  // Ajouter ou modifier la valeur de nb_table
  const handleSave = async () => {
    const validationError = validateInput(inputValue);
    if (validationError) {
      showMessage(validationError, 'error');
      return;
    }

    setSaving(true);
    try {
      const newValue = parseInt(inputValue, 10);
      const docRef = doc(db, 'settings', 'nb_table');
      
      // Ajouter à l'historique
      if (nbTables !== null) {
        setHistory(prev => [...prev, {
          id: Date.now(),
          previousValue: nbTables,
          newValue: newValue,
          timestamp: new Date(),
          action: 'Modification'
        }]);
      } else {
        setHistory(prev => [...prev, {
          id: Date.now(),
          previousValue: null,
          newValue: newValue,
          timestamp: new Date(),
          action: 'Création'
        }]);
      }
      
      await setDoc(docRef, { value: newValue });
      setInputValue('');
      setIsEditing(false);
      showMessage(`Nombre de tables mis à jour : ${newValue}`, 'success');
    } catch (error) {
      console.error('Erreur lors de la mise à jour de nb_table :', error);
      showMessage('Erreur lors de la mise à jour', 'error');
    } finally {
      setSaving(false);
    }
  };

  // Supprimer la valeur de nb_table
  const handleDelete = async () => {
    if (nbTables === null) {
      showMessage('Aucune valeur à supprimer', 'info');
      return;
    }

    setDeleting(true);
    try {
      const docRef = doc(db, 'settings', 'nb_table');
      
      // Ajouter à l'historique
      setHistory(prev => [...prev, {
        id: Date.now(),
        previousValue: nbTables,
        newValue: null,
        timestamp: new Date(),
        action: 'Suppression'
      }]);
      
      await updateDoc(docRef, { value: deleteField() });
      showMessage('Nombre de tables supprimé', 'success');
    } catch (error) {
      console.error('Erreur lors de la suppression de nb_table :', error);
      showMessage('Erreur lors de la suppression', 'error');
    } finally {
      setDeleting(false);
    }
  };

  // Commencer l'édition
  const handleEdit = () => {
    if (nbTables !== null) {
      setInputValue(nbTables.toString());
      setPreviousValue(nbTables);
      setIsEditing(true);
    }
  };

  // Annuler l'édition
  const handleCancel = () => {
    setInputValue('');
    setIsEditing(false);
    setPreviousValue(null);
  };

  // Copier la valeur actuelle
  const handleCopy = () => {
    if (nbTables !== null) {
      navigator.clipboard.writeText(nbTables.toString());
      showMessage('Valeur copiée dans le presse-papiers', 'info');
    }
  };

  // Exporter l'historique
  const handleExportHistory = () => {
    const dataStr = JSON.stringify(history, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `historique-tables-${new Date().toISOString().split('T')[0]}.json`;
    link.click();
    URL.revokeObjectURL(url);
    showMessage('Historique exporté', 'success');
  };

  if (loading) {
    return (
      <div className="manage-tables-loading-container">
        <div className="manage-tables-loading-spinner">
          <FaSpinner className="spinning" />
        </div>
        <p className="manage-tables-loading-text">Chargement de la configuration...</p>
      </div>
    );
  }

  return (
    <div className="manage-tables-container">
      {/* Header */}
      <div className="manage-tables-header">
        <div className="manage-tables-title-section">
          <FaTable className="manage-tables-title-icon" />
          <h1 className="manage-tables-title">Gestion des Tables</h1>
        </div>
        <p className="manage-tables-subtitle">
          Configuration du nombre de tables disponibles
        </p>
      </div>

      {/* Message de feedback */}
      {message && (
        <div className={`manage-tables-message manage-tables-message-${messageType}`}>
          <div className="manage-tables-message-icon">
            {messageType === 'success' && <FaCheckCircle />}
            {messageType === 'error' && <FaExclamationTriangle />}
            {messageType === 'info' && <FaInfoCircle />}
          </div>
          <span>{message}</span>
        </div>
      )}

      {/* Statut actuel */}
      <div className="manage-tables-status-section">
        <div className="manage-tables-status-card">
          <div className="manage-tables-status-header">
            <h3 className="manage-tables-status-title">
              <FaTable className="manage-tables-status-icon" />
              Configuration Actuelle
            </h3>
            <div className="manage-tables-status-actions">
              {nbTables !== null && (
                <>
                  <button
                    onClick={handleCopy}
                    className="manage-tables-action-btn manage-tables-copy-btn"
                    title="Copier la valeur"
                  >
                    <FaCopy />
                  </button>
                  <button
                    onClick={handleEdit}
                    className="manage-tables-action-btn manage-tables-edit-btn"
                    title="Modifier"
                  >
                    <FaEdit />
                  </button>
                </>
              )}
            </div>
          </div>
          <div className="manage-tables-status-content">
            <div className="manage-tables-status-value">
              {nbTables !== null ? (
                <>
                  <span className="manage-tables-number">{nbTables}</span>
                  <span className="manage-tables-unit">tables</span>
                </>
              ) : (
                <span className="manage-tables-undefined">Non configuré</span>
              )}
            </div>
            <div className="manage-tables-status-description">
              {nbTables !== null 
                ? `Le restaurant dispose de ${nbTables} table${nbTables > 1 ? 's' : ''}`
                : 'Aucun nombre de tables défini'
              }
            </div>
          </div>
        </div>
      </div>

      {/* Actions principales */}
      <div className="manage-tables-actions-section">
        <div className="manage-tables-actions-card">
          <h3 className="manage-tables-actions-title">
            <FaEdit className="manage-tables-actions-icon" />
            Modifier la Configuration
          </h3>
          
          <div className="manage-tables-form">
            <div className="manage-tables-input-group">
              <label className="manage-tables-input-label">
                Nombre de tables
              </label>
              <div className="manage-tables-input-container">
                <input
                  type="number"
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  placeholder="Entrez un nombre entre 1 et 100"
                  className="manage-tables-input"
                  min="1"
                  max="100"
                  disabled={saving}
                />
                <FaTable className="manage-tables-input-icon" />
              </div>
              <div className="manage-tables-input-help">
                Valeur entre 1 et 100 tables
              </div>
            </div>

            <div className="manage-tables-form-actions">
              <button
                onClick={handleSave}
                disabled={saving || !inputValue}
                className="manage-tables-btn manage-tables-save-btn"
              >
                {saving ? (
                  <>
                    <FaSpinner className="spinning" />
                    Enregistrement...
                  </>
                ) : (
                  <>
                    <FaSave />
                    {isEditing ? 'Mettre à jour' : 'Enregistrer'}
                  </>
                )}
              </button>

              {isEditing && (
                <button
                  onClick={handleCancel}
                  disabled={saving}
                  className="manage-tables-btn manage-tables-cancel-btn"
                >
                  <FaUndo />
                  Annuler
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Actions secondaires */}
        <div className="manage-tables-secondary-actions">
          <button
            onClick={handleDelete}
            disabled={deleting || nbTables === null}
            className="manage-tables-btn manage-tables-delete-btn"
          >
            {deleting ? (
              <>
                <FaSpinner className="spinning" />
                Suppression...
              </>
            ) : (
              <>
                <FaTrash />
                Supprimer
              </>
            )}
          </button>

          <button
            onClick={() => setShowHistory(!showHistory)}
            className="manage-tables-btn manage-tables-history-btn"
          >
            <FaHistory />
            {showHistory ? 'Masquer' : 'Afficher'} Historique
          </button>
        </div>
      </div>

      {/* Historique */}
      {showHistory && (
        <div className="manage-tables-history-section">
          <div className="manage-tables-history-card">
            <div className="manage-tables-history-header">
              <h3 className="manage-tables-history-title">
                <FaHistory className="manage-tables-history-icon" />
                Historique des Modifications
              </h3>
              {history.length > 0 && (
                <button
                  onClick={handleExportHistory}
                  className="manage-tables-export-btn"
                  title="Exporter l'historique"
                >
                  <FaDownload />
                </button>
              )}
            </div>
            
            <div className="manage-tables-history-content">
              {history.length > 0 ? (
                <div className="manage-tables-history-list">
                  {history.slice().reverse().map((item) => (
                    <div key={item.id} className="manage-tables-history-item">
                      <div className="manage-tables-history-item-header">
                        <span className="manage-tables-history-action">
                          {item.action}
                        </span>
                        <span className="manage-tables-history-date">
                          {item.timestamp.toLocaleString('fr-FR')}
                        </span>
                      </div>
                      <div className="manage-tables-history-item-content">
                        <div className="manage-tables-history-change">
                          <span className="manage-tables-history-from">
                            {item.previousValue !== null ? item.previousValue : 'Non défini'}
                          </span>
                          <span className="manage-tables-history-arrow">→</span>
                          <span className="manage-tables-history-to">
                            {item.newValue !== null ? item.newValue : 'Supprimé'}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="manage-tables-history-empty">
                  <FaHistory className="manage-tables-history-empty-icon" />
                  <p>Aucun historique disponible</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Informations */}
      <div className="manage-tables-info-section">
        <div className="manage-tables-info-card">
          <div className="manage-tables-info-header">
            <FaInfoCircle className="manage-tables-info-icon" />
            <h4>Informations</h4>
          </div>
          <div className="manage-tables-info-content">
            <ul className="manage-tables-info-list">
              <li>Le nombre de tables détermine la capacité d'accueil du restaurant</li>
              <li>Cette valeur est utilisée pour la gestion des réservations</li>
              <li>Les modifications sont synchronisées en temps réel</li>
              <li>L'historique des changements est conservé localement</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ManageTables;
